/**
 * StarRatingInline Component
 *
 * Compact inline star rating for the route bottom sheet.
 * Shows 5 tappable stars with average display and vote count.
 */

import { useState, type CSSProperties } from 'react';
import type { QualityRatingValue } from '@flash/shared';

/**
 * Props for StarRatingInline component
 */
export interface StarRatingInlineProps {
  /** User's current rating (null if not rated) */
  userRating: QualityRatingValue | null;
  /** Average rating from all users */
  averageRating: number | null;
  /** Called when user taps a star */
  onRate: (rating: QualityRatingValue) => Promise<void>;
  /** Called when user clears their rating */
  onClearRating?: () => Promise<void>;
  /** Whether rating is being submitted */
  isSubmitting?: boolean;
  /** Size of stars in pixels (default: 28) */
  starSize?: number;
}

/**
 * Compact inline star rating for route quality
 */
export const StarRatingInline = ({
  userRating,
  averageRating,
  onRate,
  onClearRating,
  isSubmitting = false,
  starSize = 28,
}: StarRatingInlineProps) => {
  const [hoverRating, setHoverRating] = useState<number | null>(null);

  // Display user's rating when hovering or rated, otherwise show average
  const displayRating = hoverRating ?? userRating ?? averageRating ?? 0;
  const isDisabled = isSubmitting;

  const handleStarClick = async (rating: number) => {
    if (isDisabled) return;
    // If clicking the same star, clear the rating
    if (rating === userRating && onClearRating) {
      await onClearRating();
      return;
    }
    await onRate(rating as QualityRatingValue);
  };

  const renderStar = (position: number) => {
    const isFilled = position <= displayRating;
    const isHalf = !isFilled && position - 0.5 <= displayRating && position > displayRating;

    return (
      <button
        key={position}
        type="button"
        onClick={() => handleStarClick(position)}
        onMouseEnter={() => !isDisabled && setHoverRating(position)}
        onMouseLeave={() => setHoverRating(null)}
        style={{
          ...starButtonStyle,
          width: starSize,
          height: starSize,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          opacity: isDisabled ? 0.5 : 1,
        }}
        disabled={isDisabled}
        aria-label={`Rate ${position} star${position > 1 ? 's' : ''}`}
      >
        <span
          style={{
            ...starIconStyle,
            fontSize: starSize,
            color: isFilled ? STAR_COLOR_FILLED : isHalf ? STAR_COLOR_HALF : STAR_COLOR_EMPTY,
          }}
        >
          {isFilled || isHalf ? '\u2605' : '\u2606'}
        </span>
      </button>
    );
  };

  return (
    <div style={containerStyle}>
      <div style={sectionTitleStyle}>How did you find it?</div>
      <div
        style={starsContainerStyle}
        onMouseLeave={() => setHoverRating(null)}
        role="group"
        aria-label="Quality rating"
      >
        {[1, 2, 3, 4, 5].map((pos) => renderStar(pos))}
      </div>
    </div>
  );
};

/**
 * Star colors
 */
const STAR_COLOR_FILLED = '#FBBF24'; // Amber-400
const STAR_COLOR_HALF = '#FCD34D'; // Amber-300
const STAR_COLOR_EMPTY = 'var(--color-border, #D1D5DB)';

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const sectionTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.9375rem',
  fontWeight: 700,
  color: 'var(--color-text-primary, #1f2937)',
};

const starsContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
};

const starButtonStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'transform 0.1s ease',
};

const starIconStyle: CSSProperties = {
  lineHeight: 1,
  display: 'block',
};

export default StarRatingInline;
